// Phase 126 — the single source of truth for "who is this signed-in user and
// what do they still have to do before the app can render?"
//
// Before this, the client answered that with four separate reads (superadmins /
// institute_members / students / phone_verified) spread across two hooks, which
// is why a cold refresh rendered three sequential full-screen loading states.
// get_account_status() collapses them into one round-trip.

import { supabase } from '../../lib/supabase';

// The generated Database types are stale (last regenerated at Phase 12), so the
// project-wide escape hatch is used here as it is in grading-api / tests-api.
// Replace with a typed .rpc() call after the next `supabase gen types` run.
interface UnknownRpc {
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
}
const rpcUnknown = supabase as unknown as UnknownRpc;

export type AppRole = 'superadmin' | 'institute' | 'student';

export interface AccountStatus {
  role: AppRole | null;
  /** An institute_members row exists at status 'pending' — awaiting approval. */
  membershipPending: boolean;
  displayName: string;
  phone: string;
  /** Storage path in the public `avatars` bucket, or null. Read by the shell on
   *  every page, which is why it rides this row rather than a second query. */
  avatarPath: string | null;
  profileComplete: boolean;
  phoneVerified: boolean;
  /** Email/password account that still has to clear the OTP gate. */
  needsOtp: boolean;
  /** A superadmin has designated an ACTIVE institute for new sign-ups. */
  defaultInstituteReady: boolean;
}

interface AccountStatusRow {
  role: string | null;
  membership_pending: boolean;
  display_name: string;
  phone: string;
  avatar_path: string | null;
  profile_complete: boolean;
  phone_verified: boolean;
  needs_otp: boolean;
  default_institute_ready: boolean;
}

function isAppRole(value: string | null): value is AppRole {
  return value === 'superadmin' || value === 'institute' || value === 'student';
}

export function parseAccountStatus(row: AccountStatusRow): AccountStatus {
  return {
    role: isAppRole(row.role) ? row.role : null,
    membershipPending: row.membership_pending,
    displayName: row.display_name,
    phone: row.phone,
    avatarPath: row.avatar_path ?? null,
    profileComplete: row.profile_complete,
    phoneVerified: row.phone_verified,
    needsOtp: row.needs_otp,
    defaultInstituteReady: row.default_institute_ready,
  };
}

function firstRow(data: unknown): AccountStatusRow {
  const rows = (data as AccountStatusRow[] | null) ?? [];
  const row = rows[0];
  if (row === undefined) {
    // get_account_status always returns exactly one row for an authenticated
    // caller. No row means the JWT was not accepted — treat it as an error
    // rather than silently synthesising a "nothing is set up" answer, which is
    // indistinguishable from a real unassigned account and would bounce the
    // user through the onboarding gates.
    throw new Error('Could not read your account status. Try signing in again.');
  }
  return row;
}

export async function fetchAccountStatus(): Promise<AccountStatus> {
  const { data, error } = await rpcUnknown.rpc('get_account_status');
  if (error !== null) throw new Error(error.message);
  return parseAccountStatus(firstRow(data));
}

export async function completeProfile(displayName: string, phone: string): Promise<AccountStatus> {
  const { data, error } = await rpcUnknown.rpc('complete_profile', {
    p_display_name: displayName,
    p_phone: phone,
  });
  if (error !== null) throw new Error(error.message);
  return parseAccountStatus(firstRow(data));
}

export type EnrollmentResult = 'enrolled' | 'already_assigned' | 'not_configured';

export async function ensureDefaultEnrollment(): Promise<EnrollmentResult> {
  const { data, error } = await rpcUnknown.rpc('ensure_default_enrollment');
  if (error !== null) throw new Error(error.message);
  return data === 'enrolled' || data === 'not_configured' ? data : 'already_assigned';
}
